import { useCallback, useRef } from 'react';
import dayjs from 'dayjs';
import { isEmpty } from 'lodash';

import { useSyncExerciseData } from '../../hooks/useSyncExerciseData';
import { useSyncMealData } from '../../hooks/useSyncMealData';
import { useSyncSleepData } from '../../hooks/useSyncSleepData';
import { useAuthContext, useMetricsContext, useWearablesContext } from '../context';
import { useRecoilValue } from 'recoil';
import { selectedDateAtom } from '../recoil';
import { getTargetTerraUserIds } from '../helpers';

export const useSyncMetricsApi = () => {
  const syncedDates = useRef<string[]>([]);
  const selectedDate = useRecoilValue(selectedDateAtom);

  const { user } = useAuthContext();
  const { syncSleepData: syncSleepDataLogic } = useSyncSleepData();
  const { syncExerciseData: syncExerciseDataLogic } = useSyncExerciseData();
  const { syncMealData: syncMealDataLogic } = useSyncMealData();
  const {
    sleepDetails,
    exerciseDetails,
    mealDetails,
    isFetchingSleep,
    isFetchingExercise,
    isFetchingMeal,
  } = useMetricsContext();
  const { apiDevices } = useWearablesContext();

  const syncWearableData = useCallback(async () => {
    if (!user.metricId || !apiDevices?.length) {
      return;
    }

    const hasSynced = syncedDates.current.includes(selectedDate.format('YYYY-MM-DD'));
    const isAfterToday = selectedDate.isAfter(dayjs(), 'day');

    if (
      isFetchingSleep ||
      isFetchingExercise ||
      isFetchingMeal ||
      hasSynced ||
      isAfterToday ||
      !sleepDetails
    ) {
      return;
    }
    const hasEmptySleepDetails = sleepDetails
      .filter(({ date }) => dayjs(date).isSameOrBefore(dayjs(), 'day'))
      .some(({ details }) => isEmpty(details));

    const startDate = selectedDate.subtract(1, 'day').format('YYYY-MM-DD');
    const endDate = selectedDate.add(1, 'day').format('YYYY-MM-DD');

    // Sync sleep data only once
    if (hasEmptySleepDetails) {
      syncSleepDataLogic({
        terraUserIds: getTargetTerraUserIds(apiDevices, 'sleep'),
        startDate,
        endDate,
      });
    }
    syncExerciseDataLogic({
      terraUserIds: getTargetTerraUserIds(apiDevices, 'exercise'),
      startDate,
      endDate,
    });
    syncMealDataLogic({
      terraUserIds: getTargetTerraUserIds(apiDevices, 'meal'),
      startDate,
      endDate,
    });

    syncedDates.current.push(selectedDate.format('YYYY-MM-DD'));
  }, [
    user,
    isFetchingSleep,
    isFetchingExercise,
    selectedDate,
    apiDevices,
    sleepDetails,
    exerciseDetails,
    mealDetails,
  ]);

  const resyncSleepData = useCallback(
    async (startDate: string, endDate: string) => {
      if (!apiDevices?.length) {
        return;
      }

      await syncSleepDataLogic({
        terraUserIds: getTargetTerraUserIds(apiDevices, 'sleep'),
        startDate,
        endDate,
      });

      syncedDates.current.push(selectedDate.format('YYYY-MM-DD'));
    },
    [apiDevices]
  );

  return {
    syncWearableData,
    resyncSleepData,
  };
};
